/**
 * A localStorage cache of the last resolved theme, applied before React
 * mounts so the very first paint already matches this person's theme
 * instead of flashing Legacy Night while identity and `GET
 * /ops/preferences` are still in flight — routinely seconds on a Starlink
 * connection, and it happens on every load and every new tab.
 *
 * This is a CACHE, never the source of truth. `p.theme` from the server
 * always wins the moment it arrives: `useThemeSync` (./theme.ts) re-applies
 * from `prefs['p.theme']` on every mount regardless of what is cached here.
 * Only the RESOLVED id (one of the four `AppliedTheme` values) is ever
 * stored — never the six-way `ThemeChoice` — so a stale "Tossker Auto"
 * cannot paint the wrong side of an OS flip before prefs arrive.
 *
 * No React import in this module, on purpose: main.tsx calls
 * `applyCachedThemeBeforeMount` as the first statement, before
 * `createRoot`/`render`, and this needs to be safe to run in that position
 * without pulling any of React's module graph in ahead of it.
 */
import { THEME_STORAGE_KEY as STORAGE_KEY, isAppliedTheme, type AppliedTheme } from '@communicator/shared'

/**
 * Best-effort read. Guarded because Safari private mode throws on ANY
 * localStorage access (not only writes), and the console also runs inside a
 * Tauri webview, which is not guaranteed to have a working localStorage
 * either. A cosmetic cache must never take startup down.
 */
export function readCachedTheme(): AppliedTheme | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return isAppliedTheme(raw) ? raw : null
  } catch {
    return null
  }
}

/** Best-effort write — see {@link readCachedTheme}'s doc comment for why this is guarded too. */
export function writeCachedTheme(theme: AppliedTheme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Ignored — a cache that can't be written is no worse than no cache.
  }
}

/**
 * Paint whatever is cached, synchronously, before React ever mounts. Called
 * once from main.tsx. A missing or invalid cache value (nothing stored yet,
 * or a garbage/hand-edited one) leaves `document.documentElement` alone —
 * the CSS's own bare `:root` fallback already paints Legacy Night, which is
 * the same "no signal yet" answer this module would otherwise have to
 * duplicate.
 *
 * NOTE: this is the SECOND line of defence, not the first. By the time any
 * module in the bundle runs, the browser has already painted a frame — a
 * `<script type="module">` is deferred by spec. The inline `<head>` script
 * from src/theme-preamble.ts is what actually prevents the flash; this call
 * remains because it costs nothing and covers any entry path that does not
 * go through the generated HTML (a test harness, an embedded host).
 */
export function applyCachedThemeBeforeMount(): void {
  const cached = readCachedTheme()
  if (cached) document.documentElement.dataset.theme = cached
}
